import AttachmentEditor from '@components/AttachmentEditor'
import ChatRequestFields from '@components/event/form/ChatRequestFields'
import TimeInputWithUnit from '@components/event/TimeInputWithUnit'
import { Localizable } from '@components/localizable/Localizable'
import { SBFormItemAddon } from '@components/SBFormItemAddon'
import { UnsplashSearch } from '@components/unsplash-search'
import { bookingMethodsOptions } from '@models/Event'
import { Plan } from '@models/membership/Plan'
import { useAppSelector } from '@store/hooks'
import { selectSelectedEvent } from '@store/membership/EventReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBRMFormActionProps } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { afterFieldRule, requiredRule } from '@utils/rules'
import {
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Segmented,
  Space,
  Switch,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

const { Text } = Typography

const Update = ({ form, formProps }: SBRMFormActionProps) => {
  const item = useAppSelector(selectSelectedEvent())
  const bookingMethod = Form.useWatch('bookingMethod', form)
  const [imageSegment, setImageSegment] = useState<
    'keep_existing' | 'unsplash' | 'file'
  >('keep_existing')

  useEffect(() => {
    form.setFieldsValue({
      ...item,
      start: dayjs(item?.start),
      end: dayjs(item?.end),
    })
  }, [item])

  if (item === undefined) {
    return <></>
  }

  return (
    <Form {...formProps} form={form} layout="vertical">
      <Form.Item
        name="id"
        hidden
        style={{ display: 'none' }}
        rules={[requiredRule()]}
      >
        <Input type="hidden" />
      </Form.Item>
      <Localizable.Input
        name={'title'}
        label={'Title'}
        rules={[requiredRule()]}
        max={255}
      />
      <Localizable.TextArea
        name={'description'}
        label={'Description'}
        rules={[requiredRule()]}
      />
      <Col span={24}>
        <Form.Item
          label={'Membership plans'}
          name="membershipPlans"
          rules={[requiredRule()]}
        >
          <SBAsyncSelect<Plan>
            entity={Entity.membershipPlan}
            multiple
            allowClear
          />
        </Form.Item>
      </Col>
      <Divider />
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item name="start" rules={[]} label={'Start date'}>
            <DatePicker showTime showSecond={false} className="w-full" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="end"
            dependencies={['start']}
            rules={[afterFieldRule('start')]}
            label={'End date'}
          >
            <DatePicker showTime showSecond={false} className="w-full" />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Space className="w-full" orientation="vertical">
        <Text className="field-required">Image</Text>
        <Segmented
          block
          value={imageSegment}
          onChange={(value) => {
            setImageSegment(value as any)
            form.setFieldValue('image', undefined)
            form.setFieldValue(['unsplash', 'url'], undefined)
            form.setFieldValue(['unsplash', 'blurHash'], undefined)
          }}
          options={[
            { value: 'keep_existing', label: 'Keep existing' },
            { value: 'unsplash', label: 'Unsplash' },
            { value: 'file', label: 'Upload image' },
          ]}
        />
        {imageSegment === 'unsplash' && <UnsplashSearch />}
        {imageSegment === 'file' && (
          <AttachmentEditor
            hideLabel
            formItem={{ name: 'image', rules: [requiredRule()] }}
            dragger={{ fileTypes: ['picture'] }}
            image={{ width: 1200, height: 675, aspect: 16 / 9 }}
          />
        )}
      </Space>
      <Divider />
      <Row gutter={[16, 16]}>
        <Col span={16}>
          <Form.Item name="address" rules={[]} label={'Address'}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="venue" rules={[]} label={'Venue'}>
            <SBAsyncSelect
              allowClear
              entity={Entity.venue}
              searchFieldName="name"
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Space className="w-full" orientation="vertical">
        <Form.Item
          name={'bookingMethod'}
          rules={[requiredRule()]}
          label="Booking method"
        >
          <Segmented block options={bookingMethodsOptions} />
        </Form.Item>
        {bookingMethod === 'chat-request' && <ChatRequestFields />}
        {bookingMethod === 'in-app' && (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <SBFormItemAddon
                formItemProps={{
                  label: 'Total Max participants',
                  name: 'maxParticipants',
                  rules: [requiredRule()],
                  required: true,
                }}
                addonAfter="participants"
              >
                <InputNumber className="w-full" />
              </SBFormItemAddon>
            </Col>
            <Col span={12}>
              <SBFormItemAddon
                formItemProps={{
                  label: 'Max members',
                  name: 'maxMembers',
                  rules: [requiredRule()],
                  required: true,
                }}
                addonAfter="members"
              >
                <InputNumber className="w-full" />
              </SBFormItemAddon>
            </Col>
            <Col span={12}>
              <SBFormItemAddon
                formItemProps={{
                  label: 'Max guests',
                  name: 'maxGuests',
                  rules: [requiredRule()],
                  required: true,
                }}
                addonAfter="guests"
              >
                <InputNumber className="w-full" />
              </SBFormItemAddon>
            </Col>
            <Col span={24}>
              <SBFormItemAddon
                formItemProps={{
                  label: 'Max guests per participant',
                  name: 'maxGuestsPerParticipant',
                  rules: [requiredRule()],
                  required: true,
                }}
                addonAfter="guests / member"
              >
                <InputNumber className="w-full" />
              </SBFormItemAddon>
            </Col>
          </Row>
        )}
      </Space>
      <Divider />
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            name={'participationCutoff'}
            label={'Cutoff - Participation'}
            rules={[requiredRule()]}
          >
            <TimeInputWithUnit
              name={'participationCutoff'}
              initialValue={item.participationCutoff}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name={'cancelParticipationCutoff'}
            label={'CancelCutoff - Participation'}
            rules={[requiredRule()]}
          >
            <TimeInputWithUnit
              name={'cancelParticipationCutoff'}
              initialValue={item.cancelParticipationCutoff}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Card>
        <Flex justify="space-between">
          <Text>Visibility</Text>
          <Form.Item
            name={'isPublished'}
            rules={[requiredRule()]}
            valuePropName="checked"
            noStyle
          >
            <Switch
              checkedChildren={'Published'}
              unCheckedChildren={'Hidden'}
            />
          </Form.Item>
        </Flex>
      </Card>
      <Divider />

      <Localizable.TextArea
        name={'reminderDescription'}
        label={'Reminder description'}
      />
    </Form>
  )
}

export default Update
